import { action, observable, makeObservable } from 'mobx';
import { WarehouseEditForm } from 'modules/warehouse/forms';
import { BaseViewStore } from 'core/stores';
import { ApplicationStore } from 'platform/application/stores';
import { CoreUserService } from 'common/data';
import { formInvalidate, isSome, generateRandomString } from 'core/utils';
import { LookupStore } from 'modules/lookup/stores';
import {
	inventoryLevelOptions,
	lookupRouteName,
	organizationTypes,
	priority1Carriers,
	sourceOfChange,
	warehouseManagementType,
} from 'common/constants';
import _ from 'lodash';
import BaasicDropdownViewStore from 'common/stores/BaasicDropdownViewStore';
import { UserStore } from 'modules/user/stores';

class WarehouseEditViewStore extends BaseViewStore {
	isUpdateAction = isSome(this.rootStore.routerStore.routerState.params.id);
	organizationTypeId = null;
	@observable infiniteSearchPhrase = null;
	@observable isHavingAPIKey = false;

	form = new WarehouseEditForm({
		onSuccess: async () => {
			this.createOrUpdateApplication();
		},
	});

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.listStore = new ApplicationStore(this.rootStore);
		this.lookupStore = new LookupStore(rootStore);
		this.coreUserService = new CoreUserService(rootStore.app.baasic.apiClient);
		this.userStore = new UserStore(rootStore);

		this.initialize();
	}

	@action
	initialize = async () => {
		this.suspend();
		this.form.reset();
		await this.initializeDropdowns();
		if (this.isUpdateAction) {
			await this.getApplication(this.rootStore.routerStore.routerState.params.id);

			if (
				this.listStore.item.warehouseNotificationRecipients &&
				this.listStore.item.warehouseNotificationRecipients.length > 0
			) {
				this.form.$('warehouseNotificationRecipient').value = this.listStore.item.warehouseNotificationRecipients.map(
					rec => {
						return { value: rec.email, label: rec.email };
					}
				);
			}

			if (this.listStore.item.apiKey != '') this.isHavingAPIKey = true;
			const additionalInfo = this.listStore.item.warehouseOrganizationInfo;
			this.listStore.item.address = additionalInfo.address;
			this.listStore.item.contact = additionalInfo.contact;

			this.form.updateForm(this.listStore.item, true);

			this.form.$('enableEmailNotification').value = additionalInfo.enableEmailNotification;
			this.form.$('cutoffTime').value = additionalInfo.cutoffTime;
			this.form.$('acceptingReturns').value = additionalInfo.acceptingReturns;
			this.form.$('allowSaturdayShipping').value = additionalInfo.allowSaturdayShipping;
			this.form.$('sendOrderAPI').value = additionalInfo.sendOrderAPI;
			this.form.$('sendInventoryAPI').value = additionalInfo.sendInventoryAPI;
			this.form.$('sendTransferAPI').value = additionalInfo.sendTransferAPI;
			if (additionalInfo.warehouseManagementType) {
				this.warehouseManagementTypeDropdownStore.setValue(additionalInfo.warehouseManagementType);
			}
			this.warehouseStatusDropdownStore.setValue(additionalInfo.warehouseStatus);
			this.inventoryLevelDropdownStore.setValue({
				value: additionalInfo.inventoryLevel,
				label: additionalInfo.inventoryLevel,
			});
			this.countryDropdownStore.setValue(additionalInfo.address.coreCountry);
			this.stateDropdownStore.setValue(additionalInfo.address.coreCountryState);
			this.timezoneDropdownStore.setValue(this.listStore.item.timezone);
			if (additionalInfo.preferredLtlCarrier) {
				const preferredLtlCarrier = _.find(priority1Carriers, { value: additionalInfo.preferredLtlCarrier });
				this.preferredLtlCarrierDropdownStore.setValue(
					preferredLtlCarrier || {
						value: additionalInfo.preferredLtlCarrier,
						label: additionalInfo.preferredLtlCarrier,
					}
				);
			}
		} else {
			this.form.$('enableEmailNotification').value = false;
			this.form.$('acceptingReturns').value = false;
			this.form.$('allowSaturdayShipping').value = false;
			this.form.$('sendOrderAPI').value = false;
			this.form.$('sendInventoryAPI').value = false;
			this.form.$('sendTransferAPI').value = false;
			this.form.$('email').set('rules', 'required|email|string');
			this.form.$('password').set('rules', 'required|string|password');
			this.form.$('confirmPassword').set('rules', 'required|string|same:password');
		}
		this.setOrganizationTypeId();
		this.resume();
	};

	@action.bound
	initializeDropdowns = async () => {
		_.each(
			[
				'warehouseManagementTypeDropdownStore',
				'countryDropdownStore',
				'stateDropdownStore',
				'timezoneDropdownStore',
				'inventoryLevelDropdownStore',
				'warehouseStatusDropdownStore',
				'preferredLtlCarrierDropdownStore',
			],
			dropdownStore => {
				this[dropdownStore] = new BaasicDropdownViewStore();
			}
		);

		await Promise.all([
			this.warehouseManagementTypeDropdownStore.initialize({
				store: new LookupStore(this.rootStore),
				field: this.form.$('warehouseOrganizationInfo.warehouseManagementTypeId'),
				lookupRoute: lookupRouteName.warehouseManagementType,
				onChangeCallback: value => {
					this.setWarehouseManagementRules(value);
					this.clearWmsFields(value);
				},
			}),
			this.countryDropdownStore.initialize({
				field: this.form.$('countryId'),
				store: new LookupStore(this.rootStore),
				lookupRoute: lookupRouteName.country,
			}),
			this.stateDropdownStore.initialize({
				field: this.form.$('stateId'),
				store: new LookupStore(this.rootStore),
				lookupRoute: lookupRouteName.state,
			}),
			this.timezoneDropdownStore.initialize({
				field: this.form.$('timezoneId'),
				store: new LookupStore(this.rootStore),
				lookupRoute: lookupRouteName.timezone,
			}),
			this.inventoryLevelDropdownStore.initialize({
				field: this.form.$('inventoryLevel'),
				staticOptions: inventoryLevelOptions,
			}),
			this.preferredLtlCarrierDropdownStore.initialize({
				field: this.form.$('preferredLtlCarrier'),
				staticOptions: priority1Carriers,
			}),
			this.warehouseStatusDropdownStore.initialize({
				field: this.form.$('warehouseStatusId'),
				store: new LookupStore(this.rootStore),
				lookupRoute: lookupRouteName.warehouseStatus,
			}),
		]);
	};

	@action.bound
	setRecipientsValue = value => {
		this.form.$('warehouseNotificationRecipient').set(value ? value : []);
	};

	@action.bound
	clearWmsFields = value => {
		if (value) {
			if (value.extra.abrv === warehouseManagementType.daVinci) {
				this.form.$('warehouseOrganizationInfo.facilityId').clear();
				this.form.$('warehouseOrganizationInfo.userLoginId').clear();
			}
			if (value.extra.abrv === warehouseManagementType.extensiv) {
				this.form.$('warehouseOrganizationInfo.warehouseId').clear();
				this.form.$('warehouseOrganizationInfo.dbName').clear();
				this.form.$('warehouseOrganizationInfo.subdomain').clear();
				this.form.$('warehouseOrganizationInfo.licenseId').clear();
				this.form.$('warehouseOrganizationInfo.userName').clear();
				this.form.$('warehouseOrganizationInfo.password').clear();
			}
		} else {
			this.form.$('warehouseOrganizationInfo').clear();
		}
	};

	@action.bound
	setWarehouseManagementRules = value => {
		if (value) {
			if (value.extra.abrv === warehouseManagementType.daVinci) {
				this.form.$('warehouseOrganizationInfo.warehouseId').set('rules', 'required');
				this.form.$('warehouseOrganizationInfo.dbName').set('rules', 'required');
				this.form.$('warehouseOrganizationInfo.subdomain').set('rules', 'required');
				this.form.$('warehouseOrganizationInfo.licenseId').set('rules', 'required');
				this.form.$('warehouseOrganizationInfo.userName').set('rules', 'required');
				this.form.$('warehouseOrganizationInfo.password').set('rules', 'required');
			} else {
				this.form.$('warehouseOrganizationInfo.warehouseId').set('rules', '');
				this.form.$('warehouseOrganizationInfo.dbName').set('rules', '');
				this.form.$('warehouseOrganizationInfo.subdomain').set('rules', '');
				this.form.$('warehouseOrganizationInfo.licenseId').set('rules', '');
				this.form.$('warehouseOrganizationInfo.userName').set('rules', '');
				this.form.$('warehouseOrganizationInfo.password').set('rules', '');
			}
			if (value.extra.abrv === warehouseManagementType.extensiv) {
				this.form.$('warehouseOrganizationInfo.facilityId').set('rules', 'required');
				this.form.$('warehouseOrganizationInfo.userLoginId').set('rules', 'required');
			} else {
				this.form.$('warehouseOrganizationInfo.facilityId').set('rules', '');
				this.form.$('warehouseOrganizationInfo.userLoginId').set('rules', '');
			}
		} else {
			this.form.$('warehouseOrganizationInfo.warehouseId').set('rules', '');
			this.form.$('warehouseOrganizationInfo.dbName').set('rules', '');
			this.form.$('warehouseOrganizationInfo.subdomain').set('rules', '');
			this.form.$('warehouseOrganizationInfo.licenseId').set('rules', '');
			this.form.$('warehouseOrganizationInfo.userName').set('rules', '');
			this.form.$('warehouseOrganizationInfo.password').set('rules', '');
			this.form.$('warehouseOrganizationInfo.facilityId').set('rules', '');
			this.form.$('warehouseOrganizationInfo.userLoginId').set('rules', '');
		}
	};

	@action.bound async setOrganizationTypeId() {
		await this.lookupStore.find({ page: 1, rpp: 15 }, lookupRouteName.organizationType);
		this.organizationTypeId = _.find(this.lookupStore.data, organizationType => {
			return organizationType.abrv.toUpperCase() == organizationTypes.warehouse.toUpperCase();
		}).id;
	}

	@action async getApplication(id) {
		await this.listStore.getApplication(id, {
			embed: [
				'timezone',
				'warehouseOrganizationInfo',
				'warehouseOrganizationInfo.warehouseStatus',
				'warehouseOrganizationInfo.address',
				'warehouseOrganizationInfo.address.coreCountry',
				'warehouseOrganizationInfo.address.coreCountryState',
				'warehouseOrganizationInfo.contact',
				'warehouseOrganizationInfo.warehouseManagementType',
				'warehouseNotificationRecipients',
			],
		});
	}

	@action.bound onTimeChange(time, field) {
		if (time && time.value && field) {
			this.form.$(field).value = time.formattedValue;
		}
	}

	@action.bound generateApiKey() {
		this.isHavingAPIKey = true;
		var radnomStringGenerated = generateRandomString(32);
		this.form.$('apiKey').set('value', radnomStringGenerated);
	}

	@action async createOrUpdateApplication() {
		this.suspend();
		const {
			identifier,
			applicationTitle,
			address: { address1, address2, address3, city, zipCode },
			countryId,
			stateId,
			timezoneId,
			contact,
			warehouseOrganizationInfo,
			warehouseStatusId,
			cutoffTime,
			acceptingReturns,
			allowSaturdayShipping,
			sendOrderAPI,
			sendInventoryAPI,
			sendTransferAPI,
			email,
			password,
			confirmPassword,
			apiKey,
			enableEmailNotification,
			warehouseNotificationRecipient,
			inventoryLevel,
			preferredLtlCarrier,
		} = this.form.values();

		let warehouseNotificationRecipients;
		if (warehouseNotificationRecipient) {
			warehouseNotificationRecipients = warehouseNotificationRecipient.map(email => {
				return {
					warehouseId: _.get(this.listStore, 'item.id'),
					email: email.label,
				};
			});
		}

		let warehouseManagementCredentials = {};
		if (this.warehouseManagementTypeDropdownStore.value) {
			if (this.warehouseManagementTypeDropdownStore.value.extra.abrv === warehouseManagementType.daVinci) {
				warehouseManagementCredentials = {
					warehouseId: warehouseOrganizationInfo.warehouseId,
					dbName: warehouseOrganizationInfo.dbName,
					subdomain: warehouseOrganizationInfo.subdomain,
					licenseId: warehouseOrganizationInfo.licenseId,
					userName: warehouseOrganizationInfo.userName,
					password: warehouseOrganizationInfo.password,
				};
			} else if (this.warehouseManagementTypeDropdownStore.value.extra.abrv === warehouseManagementType.extensiv) {
				warehouseManagementCredentials = {
					facilityId: warehouseOrganizationInfo.facilityId,
					userLoginId: warehouseOrganizationInfo.userLoginId,
				};
			}
		}

		let addressObj = {
			address1,
			address2,
			address3,
			city,
			zipCode,
			coreCountryStateId: stateId,
			coreCountryId: countryId,
		};
		var organization = {
			identifier,
			applicationTitle,
			organizationTypeId: this.organizationTypeId,
			apiKey,
			isActive: this.warehouseStatusDropdownStore.value.extra.abrv === 'open' ? true : false,
			timezoneId: timezoneId,
			timezone: this.timezoneDropdownStore.value.extra,
			warehouseNotificationRecipients,
		};
		var organizationInfo = {
			...warehouseManagementCredentials,
			address: addressObj,
			contact,
		};

		if (!this.isUpdateAction) {
			organization = {
				...organization,
				membershipData: {
					email,
					password,
				},

				coreUserInfo: {
					piiAccess: true,
				},

				confirmPassword,
			};
		} else {
			const additionalInfo = this.listStore.item.warehouseOrganizationInfo;
			organizationInfo = {
				...warehouseManagementCredentials,
				isAvailable: additionalInfo.isAvailable,
				sendOrderAPI: additionalInfo.sendOrderAPI,
				sendTransferAPI: additionalInfo.sendTransferAPI,
				sendInventoryAPI: additionalInfo.sendInventoryAPI,
				useAutoExitScan: additionalInfo.useAutoExitScan,
				useRegularPalletizingMode: additionalInfo.useRegularPalletizingMode,
				id: additionalInfo.id,
				addressId: additionalInfo.address.id,
				contactId: additionalInfo.contact.id,
				address: addressObj,
				contact: contact,
			};
		}
		organization.warehouseOrganizationInfo = {
			...organizationInfo,
			enableEmailNotification: enableEmailNotification,
			cutoffTime,
			acceptingReturns: acceptingReturns,
			allowSaturdayShipping: allowSaturdayShipping,
			sendOrderAPI: sendOrderAPI,
			sendInventoryAPI: sendInventoryAPI,
			sendTransferAPI: sendTransferAPI,
			inventoryLevel: inventoryLevel,
			preferredLtlCarrier: preferredLtlCarrier || null,
			warehouseStatusId: warehouseStatusId,
			warehouseStatus: this.warehouseStatusDropdownStore.value.extra,
			warehouseManagementTypeId: warehouseOrganizationInfo.warehouseManagementTypeId,
		};
		if (
			!this.isUpdateAction &&
			password
				.trim()
				.toLowerCase()
				.includes(
					email
						.split('@')[0]
						.toLowerCase()
						.trim()
				)
		) {
			this.form.invalidate('PASSWORDCONTAINSUNSERNAME');
		} else {
			try {
				this.isUpdateAction
					? await this.listStore.updateApplication(
							{
								id: this.rootStore.routerStore.routerState.params.id,
								...organization,
							},
							sourceOfChange.editWarehouse
					  )
					: await this.listStore.createApplication(organization, sourceOfChange.createWarehouse);
				this.rootStore.notificationStore.success(
					this.isUpdateAction
						? ['LOOKUPUPDATE.WAREHOUSE', { name: organization.identifier }]
						: ['LOOKUPCREATE.WAREHOUSE', { name: organization.identifier }]
				);

				if (!this.isUpdateAction) {
					this.rootStore.notificationStore.warning(['WAREHOUSEVIEW.ADDSELLERWAREHOUSES']);
				}
				this.rootStore.routerStore.goTo('master.app.main.warehouse.list');
			} catch (err) {
				formInvalidate({ form: this.form, err });
			}
		}
		this.resume();
	}
}

export default WarehouseEditViewStore;
